import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import Logo from '@/components/brand/logo'
import Menu from '@/components/generals/menu'
import RollText from '@/components/generals/roll-text'
import LocalTime from '@/components/generals/local-time'
import TransitionLink from '@/components/motion/transition-link'
import { navItems } from '@/constants/site'

// Fixed header. It steps out of the way while reading down the page, returns on
// the way back up, and takes on the colour of whichever environment is active.
const Header: FC = (): ReactNode => {
    const router = useRouter()
    const toggleRef = useRef<HTMLButtonElement>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        let last = window.scrollY
        let frame = 0

        const onScroll = (): void => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                const y = window.scrollY

                setScrolled(y > 24)

                if (Math.abs(y - last) > 6) {
                    setHidden(y > last && y > 240)
                    last = y
                }
            })
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)
            cancelAnimationFrame(frame)
        }
    }, [])

    useEffect(() => {
        const close = (): void => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const closeMenu = useCallback((): void => setMenuOpen(false), [])

    const currentPath = router.asPath.split(/[?#]/)[0]

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 text-fore transition-[translate,background-color] duration-700 ease-quart motion-reduce:transition-none',
                    hidden && !menuOpen && '-translate-y-full',
                    scrolled && !menuOpen && 'bg-canvas/80 backdrop-blur-md'
                )}
            >
                <a
                    href='#main'
                    className='type-label sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-10 focus:bg-volt focus:px-3 focus:py-2 focus:text-ink'
                >
                    Skip to content
                </a>

                <div className='flex h-16 items-center justify-between gap-6 px-5 md:h-20 md:px-8'>
                    <TransitionLink href='/' aria-label='Fit Design home' className='group flex items-center gap-4'>
                        <Logo className='h-6 md:h-7' />

                        <span className='type-label hidden border-l border-rule pl-4 leading-tight text-quiet xl:block'>
                            Award-winning
                            <br />
                            design studio
                        </span>
                    </TransitionLink>

                    <nav aria-label='Main' className='hidden lg:block'>
                        <ul className='flex items-center gap-9'>
                            {navItems.map((item, index) => {
                                const active = currentPath === item.href || currentPath.startsWith(`${item.href}/`)

                                return (
                                    <li key={item.href}>
                                        <TransitionLink
                                            href={item.href}
                                            aria-current={active ? 'page' : undefined}
                                            className='group flex items-start gap-1.5 text-[0.95rem] font-medium tracking-[-0.01em]'
                                        >
                                            <span
                                                aria-hidden='true'
                                                className={cn(
                                                    'mt-[0.5em] size-1.5 bg-pop transition-transform duration-500 ease-expo',
                                                    active ? 'scale-100' : 'scale-0'
                                                )}
                                            />

                                            <RollText>
                                                {item.label}
                                            </RollText>

                                            <span className='type-label text-[0.6rem] text-quiet'>
                                                0{index + 1}
                                            </span>
                                        </TransitionLink>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-3'>
                        <p className='type-label hidden items-center gap-2 text-quiet xl:flex'>
                            <span>
                                London
                            </span>

                            <LocalTime zone='Europe/London' className='text-fore' />
                        </p>

                        <TransitionLink
                            href='/contact?service=free-homepage-preview'
                            className='group hidden items-center gap-2 bg-fore px-4 py-3 text-sm font-medium text-canvas md:inline-flex'
                        >
                            <RollText>
                                Request a preview
                            </RollText>

                            <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-500 ease-expo group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                        </TransitionLink>

                        <button
                            ref={toggleRef}
                            type='button'
                            aria-expanded={menuOpen}
                            aria-controls='site-menu'
                            onClick={() => setMenuOpen(open => !open)}
                            className='flex h-11 items-center gap-3 pl-2 lg:hidden'
                        >
                            <span className='type-label'>
                                {menuOpen ? 'Close' : 'Menu'}
                            </span>

                            <span aria-hidden='true' className='relative block h-3 w-6'>
                                <span
                                    className={cn(
                                        'absolute left-0 top-0 h-px w-6 bg-current transition-transform duration-500 ease-expo',
                                        menuOpen && 'translate-y-1.5 rotate-45'
                                    )}
                                />

                                <span
                                    className={cn(
                                        'absolute bottom-0 left-0 h-px w-6 bg-current transition-transform duration-500 ease-expo',
                                        menuOpen && '-translate-y-1.5 -rotate-45'
                                    )}
                                />
                            </span>
                        </button>
                    </div>
                </div>
            </header>

            <Menu open={menuOpen} onClose={closeMenu} toggleRef={toggleRef} />
        </>
    )
}

export default Header
